/**
 * The application landing page/home page.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Card from '@amzn/meridian/card';
import Loader from '@amzn/meridian/loader';
import Heading from '@amzn/meridian/heading';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import Responsive from '@amzn/meridian/responsive';
import Icon from '@amzn/meridian/icon';
import arrowRightLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-right-large';
import { PageLayout } from '../components/page-layout';
import { useSites } from '../hooks/use-sites';
import { escapeRegExp } from '../utils/regex-utils';
import prismLogo from '../images/prism_logo.png';


export const Landing = () => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [site, setSite] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [alertMessage, setAlertMessage] = useState<{type: string, message: string} | null>(null);
    const { data: sites, isLoading: sitesLoading, error: sitesError, retry } = useSites();

    // Filter sites based on search query
    const searchRegExp = new RegExp(escapeRegExp(searchQuery), "i");
    const filteredSites = sites?.filter(
        siteCode => !searchQuery || searchRegExp.test(siteCode)
    ) || [];

    const handleSiteAccess = useCallback(() => {
        const trimmedSite = site.trim();
        if (!trimmedSite) {
            setAlertMessage({ type: "error", message: "Site code is required" });
            return;
        }

        setAlertMessage(null);
        setLoading(true);
        navigate(`/equipment-status?site=${encodeURIComponent(trimmedSite.toUpperCase())}`);
    }, [site, navigate]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSiteAccess();
        }
    }, [handleSiteAccess]);

    if (isLoading) {
        return (
            <PageLayout>
                <Column alignmentHorizontal="center" spacing="400">
                    <Loader size="large" />
                    <Text type="h200">Loading PRISM Dashboard...</Text>
                </Column>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div style={{ 
                minHeight: '80vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
            }}>
                <Column 
                    spacing="500" 
                    alignmentHorizontal="center"
                    spacingInset="400"
                >
                
                {/* Hero Section */}
                <Row 
                    spacing="400" 
                    alignmentVertical="center" 
                    alignmentHorizontal="center"
                >
                    <img src={prismLogo} alt="PRISM logo" width="120" height="120" />
                    <Column spacing="200" alignmentHorizontal="center">
                        <Heading level={1} type="d100">PRISM</Heading>
                        <Heading level={3} type="h300" color="secondary">
                            Predictive Insight System for Maintenance
                        </Heading>
                        <Text type="b200" color="secondary">
                            Real-time equipment monitoring for Amazon fulfillment centers
                        </Text>
                    </Column>
                </Row>

                {/* Mobile Responsive */}
                <Responsive
                    query="max-width"
                    props={{
                        isMobile: { default: false, '767px': true }
                    }}
                >
                    {({ isMobile }) => (
                        <Card 
                            spacingInset="600"
                            width={isMobile ? "100%" : "800px"}
                            
                        >
                    <Column spacing="300" alignmentHorizontal="center">
                        <Heading level={4} type="h400">Access Equipment Deepdive</Heading>
                        <Text type="b200" color="secondary">
                            Enter your fulfillment center site code to monitor equipment status
                        </Text>
                        
                        {/* Sites Loading State */}
                        {sitesLoading && (
                            <Column spacing="200" alignmentHorizontal="center">
                                <Row spacing="200">
                                    <Loader size="small" />
                                    <Text type="b200">Loading sites...</Text>
                                </Row>
                            </Column>
                        )}

                        {/* Sites Error State with Manual Entry Fallback */}
                        {sitesError && (
                            <Column spacing="300">
                                <Alert type="warning">
                                    <Column spacing="200">
                                        <Text type="b200">Could not load site list. You can enter a site code manually below.</Text>
                                        <Button type="secondary" size="small" onClick={retry}>
                                            Retry Loading Sites
                                        </Button>
                                    </Column>
                                </Alert>
                                
                                {/* Manual Input Fallback with Submit Button */}
                                {isMobile ? (
                                    <Column spacing="300" alignmentHorizontal="center">
                                        <Input 
                                            label="Site Code (Manual Entry)"
                                            value={site}
                                            onChange={setSite}
                                            onKeyDown={handleKeyDown}
                                            placeholder="e.g., BFI4, LGB3, SEA6"
                                            size="large"
                                            width="100%"
                                        />
                                        <Button 
                                            onClick={handleSiteAccess}
                                            disabled={!site.trim()}
                                            size="large"
                                            aria-label="Access Dashboard"
                                        >
                                            <Icon tokens={arrowRightLargeTokens} />
                                        </Button>
                                    </Column>
                                ) : (
                                    <Row 
                                        spacing="300" 
                                        alignmentVertical="bottom" 
                                        alignmentHorizontal="center"
                                    >
                                        <Input 
                                            label="Site Code (Manual Entry)"
                                            value={site}
                                            onChange={setSite}
                                            onKeyDown={handleKeyDown}
                                            placeholder="e.g., BFI4, LGB3, SEA6"
                                            size="large"
                                            width="300px"
                                        />
                                        <Button 
                                            onClick={handleSiteAccess}
                                            disabled={!site.trim()}
                                            size="large"
                                            aria-label="Access Dashboard"
                                        >
                                            <Icon tokens={arrowRightLargeTokens} />
                                        </Button>
                                    </Row>
                                )}
                            </Column>
                        )}

                        {/* Site Selection - Only show when sites are loaded successfully */}
                        {!sitesLoading && !sitesError && sites && (
                            <>
                                {/* Mobile Responsive */}
                                {isMobile ? (
                                    <Column spacing="300" alignmentHorizontal="center">
                                        <Select
                                            label="Site Code"
                                            value={site}
                                            onChange={setSite}
                                            searchQuery={searchQuery}
                                            onSearch={setSearchQuery}
                                            placeholder="e.g., BFI4, LGB3, SEA6"
                                            size="large"
                                            width="100%"
                                        >
                                            {filteredSites.map(siteCode => (
                                                <SelectOption
                                                    key={siteCode}
                                                    value={siteCode}
                                                    label={siteCode}
                                                />
                                            ))}
                                            {!filteredSites.length && searchQuery && (
                                                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                                                    <Text alignment="center">No sites found</Text>
                                                </Column>
                                            )}
                                        </Select>
                                        <Button 
                                            onClick={handleSiteAccess}
                                            disabled={!site.trim()}
                                            size="large"
                                            aria-label="Access Dashboard"
                                        >
                                            <Icon tokens={arrowRightLargeTokens} />
                                        </Button>
                                    </Column>
                                ) : (
                                    <Row 
                                        spacing="300" 
                                        alignmentVertical="bottom" 
                                        alignmentHorizontal="center"
                                    >
                                        <Select
                                            label="Site Code"
                                            value={site}
                                            onChange={setSite}
                                            searchQuery={searchQuery}
                                            onSearch={setSearchQuery}
                                            placeholder="e.g., BFI4, LGB3, SEA6"
                                            size="large"
                                            width="300px"
                                        >
                                            {filteredSites.map(siteCode => (
                                                <SelectOption
                                                    key={siteCode}
                                                    value={siteCode}
                                                    label={siteCode}
                                                />
                                            ))}
                                            {!filteredSites.length && searchQuery && (
                                                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                                                    <Text alignment="center">No sites found</Text>
                                                </Column>
                                            )}
                                        </Select>
                                        <Button 
                                            onClick={handleSiteAccess}
                                            disabled={!site.trim()}
                                            size="large"
                                            aria-label="Access Dashboard"
                                        >
                                            <Icon tokens={arrowRightLargeTokens} />
                                        </Button>
                                    </Row>
                                )}
                            </>
                        )}


                        {alertMessage && (
                            <Alert type={alertMessage.type as any}>
                                {alertMessage.message}
                            </Alert>
                        )}
                    </Column>
                        </Card>
                    )}
                </Responsive>

                </Column>
            </div>
        </PageLayout>
    );
};
